import { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { format } from 'date-fns';
import { useNavigate, useLocation } from 'react-router-dom';
import { useDropzone } from 'react-dropzone';
import imageCompression from 'browser-image-compression';
import { useAtom } from 'jotai';
import { CiCircleMinus } from 'react-icons/ci';
import { FaPen } from 'react-icons/fa6';
import MainURL from '../../../../MainURL';
import ImageURL from '../../../../ImageURL';
import Loading from '../../../../components/Loading';
import { DropdownBox } from '../../../../components/DropdownBox';
import { useKakaoAddress } from '../../../recruit/common/useKakaoAddress';
import { moveListItem } from '../../../retreat/retreatRequestImageUtils';
import { parseRetreatImages } from '../../../../Admin/retreat/retreatAdminUtils';
import { userDataAtom } from '../../../../AtomStore';
import MypageMenu from '../../common/MypageMenu';
import '../../../retreat/place/Place.scss';
import '../../../retreat/casting/Casting.scss';
import '../../../retreat/review/Review.scss';
import '../../common/Mypage.scss';

type PostType = 'place' | 'casting' | 'review';

type ImageEntry = {
  name: string;
  file?: File;
};

const sortOptions = [
  { value: '선택', label: '선택' },
  { value: '기도원', label: '기도원' },
  { value: '교회', label: '교회' },
  { value: '펜션', label: '펜션' },
  { value: '수련원/수양관/연수원', label: '수련원/수양관/연수원' },
  { value: '리조트/호텔', label: '리조트/호텔' },
];

const regionOptions = [
  { value: '선택', label: '선택' },
  { value: '서울/경기도', label: '서울/경기도' },
  { value: '강원도', label: '강원도' },
  { value: '대전/충청도', label: '대전/충청도' },
  { value: '광주/전라도', label: '광주/전라도' },
  { value: '대구/부산/경상도', label: '대구/부산/경상도' },
  { value: '제주도', label: '제주도' },
];

const sizeOptions = [
  { value: '선택', label: '선택' },
  { value: '50명이하', label: '50명이하' },
  { value: '50~100명', label: '50~100명' },
  { value: '100명이상', label: '100명이상' },
];

const castingSortOptions = [
  { value: '선택', label: '선택' },
  { value: '설교자', label: '설교자' },
  { value: '찬양사역자', label: '찬양사역자' },
  { value: '특강강사', label: '특강강사' },
  { value: '기타', label: '기타' },
];

const getImageBase = (postType: PostType) => {
  if (postType === 'place') return `${ImageURL}/images/retreat/placeimage`;
  if (postType === 'casting') return `${ImageURL}/images/retreat/castingimage`;
  return `${ImageURL}/images/retreat/postimage`;
};

const getFetchUrl = (postType: PostType) => {
  if (postType === 'place') return `${MainURL}/retreat/getuserpostpart`;
  if (postType === 'casting') return `${MainURL}/retreatcasting/getuserpostpart`;
  return `${MainURL}/retreatreview/getuserpostpart`;
};

const getUpdateUrl = (postType: PostType) => {
  if (postType === 'place') return `${MainURL}/retreat/userupdate`;
  if (postType === 'casting') return `${MainURL}/retreatcasting/userupdate`;
  return `${MainURL}/retreatreview/userupdate`;
};

export default function RetreatPostingEdit() {
  const navigate = useNavigate();
  const location = useLocation();
  const [userData] = useAtom(userDataAtom);

  const postType = (new URLSearchParams(location.search).get('type') || 'review') as PostType;
  const postId = Number(new URLSearchParams(location.search).get('id'));

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savedAddress, setSavedAddress] = useState('');

  const [placeName, setPlaceName] = useState('');
  const [placeSort, setPlaceSort] = useState('선택');
  const [region, setRegion] = useState('선택');
  const [locationText, setLocationText] = useState('');
  const [size, setSize] = useState('선택');
  const [phone, setPhone] = useState('');
  const [homepage, setHomepage] = useState('');
  const [userContact, setUserContact] = useState('');

  const {
    postcode,
    address,
    addressExtra,
    addressGuide,
    addressDetail,
    setAddressDetail,
    addressDetailRef,
    handleAddressSearch,
    getFullAddress,
    resetFromSaved,
  } = useKakaoAddress({
    onRegion: (value) => setLocationText(value),
  });

  const [castingSort, setCastingSort] = useState('선택');
  const [name, setName] = useState('');
  const [profile, setProfile] = useState('');

  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');

  const [imageEntries, setImageEntries] = useState<ImageEntry[]>([]);
  const [imageLoading, setImageLoading] = useState(false);

  const dateStamp = useMemo(() => format(new Date(), 'yyMMddHHmmss'), []);

  const onDrop = useCallback(async (acceptedFiles: File[]) => {
    if (acceptedFiles.length === 0) return;

    try {
      setImageLoading(true);
      const options = {
        maxSizeMB: 1,
        maxWidthOrHeight: 1000,
      };
      const resizedFiles = await Promise.all(
        acceptedFiles.map(async (file) => imageCompression(file, options))
      );
      const regexCopy = /[^\w!@#$%^&*()=+{}|;:'",.<>-]/g;
      const userIdCopy = userData.userAccount.slice(0, 5);

      const newEntries: ImageEntry[] = resizedFiles.map((resizedFile, index) => {
        const regex = acceptedFiles[index].name.replace(regexCopy, '');
        const regexSlice = regex.slice(-15);
        const imageName =
          postType === 'review'
            ? `${dateStamp}${userIdCopy}_${regexSlice}`
            : `${dateStamp}_${regexSlice}`;
        return {
          name: imageName,
          file: new File([resizedFile], imageName, { type: acceptedFiles[index].type }),
        };
      });

      setImageEntries((prev) => [...prev, ...newEntries]);
    } catch (error) {
      console.error('이미지 리사이징 중 오류 발생:', error);
      alert('이미지 처리 중 오류가 발생했습니다.');
    } finally {
      setImageLoading(false);
    }
  }, [dateStamp, postType, userData.userAccount]);

  const { getRootProps, getInputProps } = useDropzone({ onDrop });

  useEffect(() => {
    if (!postId || !userData.userAccount) {
      setLoading(false);
      return;
    }

    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const res = await axios.post(getFetchUrl(postType), {
          id: postId,
          userAccount: userData.userAccount,
        });

        if (cancelled) return;

        if (!res.data) {
          alert('게시글을 불러오지 못했습니다.');
          navigate('/mypage/retreatpostingmanage');
          return;
        }

        const row = res.data;
        setImageEntries(
          parseRetreatImages(row.images).map((name) => ({
            name,
          }))
        );

        if (postType === 'place') {
          setPlaceName(row.placeName || '');
          setPlaceSort(row.sort || '선택');
          setRegion(row.region || '선택');
          setLocationText(row.location || '');
          setSize(row.size || '선택');
          setSavedAddress(row.address || '');
          resetFromSaved(row.address || '');
          setPhone(row.phone || '');
          setHomepage(row.homepage || '');
          setUserContact(row.userContact || '');
        } else if (postType === 'casting') {
          setCastingSort(row.sort || '선택');
          setName(row.name || '');
          setPhone(row.phone || '');
          setProfile(row.profile || '');
          setUserContact(row.userContact || '');
        } else {
          setTitle(row.title || '');
          setContent(row.content || '');
        }
      } catch (error) {
        console.error(error);
        if (!cancelled) {
          alert('게시글을 불러오지 못했습니다.');
          navigate('/mypage/retreatpostingmanage');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [postId, postType, userData.userAccount, navigate, resetFromSaved]);

  const deleteImage = useCallback((idx: number) => {
    setImageEntries((prev) => prev.filter((_, index) => index !== idx));
  }, []);

  const moveImage = useCallback((idx: number, direction: 'up' | 'down') => {
    setImageEntries((prev) => moveListItem(prev, idx, direction));
  }, []);

  const appendFormFields = (formData: FormData) => {
    formData.append('id', String(postId));
    formData.append('userAccount', userData.userAccount);
    formData.append('images', JSON.stringify(imageEntries.map((entry) => entry.name)));

    imageEntries.forEach((entry) => {
      if (entry.file) {
        formData.append('img', entry.file);
      }
    });

    if (postType === 'place') {
      const resolvedAddress = getFullAddress().trim() || savedAddress.trim();
      formData.append('placeName', placeName.trim());
      formData.append('sort', placeSort);
      formData.append('region', region);
      formData.append('location', locationText.trim());
      formData.append('size', size);
      formData.append('address', resolvedAddress);
      formData.append('phone', phone.trim());
      formData.append('homepage', homepage.trim());
      formData.append('userContact', userContact.trim() || userData.userAccount);
    } else if (postType === 'casting') {
      formData.append('sort', castingSort);
      formData.append('name', name.trim());
      formData.append('phone', phone.trim());
      formData.append('profile', profile.trim());
      formData.append('userContact', userContact.trim() || userData.userAccount);
    } else {
      formData.append('title', title.trim());
      formData.append('content', content);
    }
  };

  const goBack = () => {
    navigate('/mypage/retreatpostingmanage');
    window.scrollTo(0, 0);
  };

  const saveEdit = async () => {
    if (!userData.userAccount || !postId) return;

    const resolvedAddress = getFullAddress().trim() || savedAddress.trim();

    if (postType === 'place') {
      if (!placeName.trim() || placeSort === '선택' || region === '선택' || size === '선택' || !resolvedAddress) {
        alert('필수 항목을 입력해주세요.');
        return;
      }
    } else if (postType === 'casting') {
      if (castingSort === '선택' || !name.trim() || !profile.trim()) {
        alert('필수 항목을 입력해주세요.');
        return;
      }
    } else if (!title.trim() || !content.trim()) {
      alert('제목과 내용을 입력해주세요.');
      return;
    }

    setSaving(true);
    try {
      const formData = new FormData();
      appendFormFields(formData);

      const res = await axios.post(getUpdateUrl(postType), formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      if (res.data) {
        alert('수정되었습니다.');
        goBack();
      } else {
        alert('수정에 실패했습니다.');
      }
    } catch (error) {
      console.error(error);
      alert('수정에 실패했습니다.');
    } finally {
      setSaving(false);
    }
  };

  const pageTitle =
    postType === 'place' ? '장소 수정' : postType === 'casting' ? '강사 수정' : '후기 수정';

  const imageBase = getImageBase(postType);

  const renderImageSection = () => (
    <div className="imageInputBox">
      <div {...getRootProps()} className="imageDropzoneStyle">
        <input {...getInputProps()} />
        <p>{imageLoading ? '이미지 처리 중...' : '+ 사진 추가'}</p>
      </div>
      {imageEntries.length > 1 && (
        <p className="imageInputBox__hint">▲▼ 버튼으로 사진 순서를 변경할 수 있습니다. 첫 번째 사진이 대표 이미지입니다.</p>
      )}
      {imageEntries.map((entry, index) => (
        <div key={`${entry.name}-${index}`} className="imagebox">
          {imageEntries.length > 1 ? (
            <div className="imagebox__order">
              <button
                type="button"
                className="imagebox__order-btn"
                aria-label="위로 이동"
                disabled={index === 0}
                onClick={() => moveImage(index, 'up')}
              >
                ▲
              </button>
              <span className="imagebox__order-num">{index + 1}</span>
              <button
                type="button"
                className="imagebox__order-btn"
                aria-label="아래로 이동"
                disabled={index === imageEntries.length - 1}
                onClick={() => moveImage(index, 'down')}
              >
                ▼
              </button>
            </div>
          ) : (
            <span className="imagebox__order-placeholder" />
          )}
          <img
            src={entry.file ? URL.createObjectURL(entry.file) : `${imageBase}/${entry.name}`}
            alt={entry.name}
          />
          <p>{entry.name}</p>
          <button type="button" onClick={() => deleteImage(index)}>
            <CiCircleMinus color="#ff0000" size={20} />
          </button>
        </div>
      ))}
    </div>
  );

  const renderPlaceForm = () => (
    <>
      <div className="place-request__notice">
        <p>수정된 내용은 운영진 검토 후 반영될 수 있습니다.</p>
        <p>자료에 이상이 있을 경우 작성자 연락처로 연락드릴 수 있습니다.</p>
      </div>

      <div className="place-request__form">
        <div className="inputbox">
          <label>형식</label>
          <DropdownBox widthmain="100%" height="46px" selectedValue={placeSort} options={sortOptions} handleChange={(e: any) => setPlaceSort(e.target.value)} />
        </div>
        <div className="inputbox">
          <label>지역</label>
          <DropdownBox widthmain="100%" height="46px" selectedValue={region} options={regionOptions} handleChange={(e: any) => setRegion(e.target.value)} />
        </div>
        <div className="inputbox">
          <label>장소명</label>
          <input type="text" value={placeName} onChange={(e) => setPlaceName(e.target.value)} />
        </div>
        <div className="inputbox">
          <label>크기</label>
          <DropdownBox widthmain="100%" height="46px" selectedValue={size} options={sizeOptions} handleChange={(e: any) => setSize(e.target.value)} />
        </div>

        <div className="inputbox inputbox--address">
          <label>주소</label>
          <div className="place-request__address-fields">
            <div className="place-request__address-row place-request__address-row--postcode">
              <input
                type="text"
                value={postcode}
                placeholder="우편번호"
                readOnly
                onClick={() => {
                  if (!postcode.trim()) {
                    handleAddressSearch();
                  }
                }}
              />
              <button type="button" className="place-request__address-search-btn" onClick={handleAddressSearch}>
                우편번호 찾기
              </button>
            </div>
            <input type="text" value={address} placeholder="주소" readOnly />
            {addressExtra.trim() !== '' && (
              <input type="text" value={addressExtra} placeholder="참고항목" readOnly className="place-request__address-extra" />
            )}
            {addressGuide.trim() !== '' && (
              <p className="place-request__address-guide">{addressGuide}</p>
            )}
            <input
              ref={addressDetailRef}
              type="text"
              value={addressDetail}
              placeholder="상세주소"
              onChange={(e) => setAddressDetail(e.target.value)}
            />
          </div>
        </div>

        <div className="inputbox">
          <label>위치</label>
          <input type="text" value={locationText} onChange={(e) => setLocationText(e.target.value)} />
        </div>
        <div className="inputbox">
          <label>장소연락처</label>
          <input type="text" value={phone} onChange={(e) => setPhone(e.target.value)} />
        </div>
        <div className="inputbox">
          <label>홈페이지</label>
          <input type="text" value={homepage} onChange={(e) => setHomepage(e.target.value)} />
        </div>
        <div className="inputbox">
          <label>작성자 연락처</label>
          <input type="text" value={userContact} onChange={(e) => setUserContact(e.target.value)} />
        </div>

        {renderImageSection()}

        <div className="buttonbox">
          <button type="button" className="button" disabled={saving} onClick={saveEdit}>
            {saving ? '수정 중...' : '수정 완료'}
          </button>
        </div>
      </div>
    </>
  );

  const renderCastingForm = () => (
    <>
      <div className="place-request__notice">
        <p>수정된 내용은 운영진 검토 후 반영될 수 있습니다.</p>
        <p>자료에 이상이 있을 경우 작성자 연락처로 연락드릴 수 있습니다.</p>
      </div>

      <div className="place-request__form">
        <div className="inputbox">
          <label>구분</label>
          <DropdownBox widthmain="100%" height="46px" selectedValue={castingSort} options={castingSortOptions} handleChange={(e: any) => setCastingSort(e.target.value)} />
        </div>
        <div className="inputbox">
          <label>강사명</label>
          <input type="text" value={name} onChange={(e) => setName(e.target.value)} />
        </div>
        <div className="inputbox">
          <label>연락처</label>
          <input type="text" value={phone} onChange={(e) => setPhone(e.target.value)} />
        </div>
        <div className="inputbox inputbox--textarea">
          <label>프로필</label>
          <textarea value={profile} onChange={(e) => setProfile(e.target.value)} />
        </div>
        <div className="inputbox">
          <label>작성자 연락처</label>
          <input type="text" value={userContact} onChange={(e) => setUserContact(e.target.value)} />
        </div>

        {renderImageSection()}

        <div className="buttonbox">
          <button type="button" className="button" disabled={saving} onClick={saveEdit}>
            {saving ? '수정 중...' : '수정 완료'}
          </button>
        </div>
      </div>
    </>
  );

  const renderReviewForm = () => (
    <div className="subpage__main__content">
      <div className="review-post">
        <div className="warningBox">
          <p>장난스러운 글이나 불건전한 내용 작성 시 경고 없이 삭제될 수 있습니다.</p>
        </div>

        <div className="userBox">
          <FaPen color="#334968" />
          <p>{userData.userNickName || userData.userAccount}</p>
        </div>

        <div className="addPostBox">
          <div className="addPostBox__title">
            <p>제목</p>
            <h5>* 최대 200자</h5>
          </div>
          <input
            value={title}
            className="inputdefault"
            type="text"
            maxLength={200}
            onChange={(e) => setTitle(e.target.value)}
          />

          <div className="addPostBox__title">
            <p>본문</p>
            <h5>* 최대 2000자</h5>
          </div>
          <textarea
            className="textarea textareapost"
            value={content}
            maxLength={2000}
            onChange={(e) => setContent(e.target.value)}
          />
        </div>

        <p className="review-post__guide">* 사진 추가, 순서 변경, 삭제가 가능합니다.</p>

        {renderImageSection()}

        <div className="buttonbox">
          <button type="button" className="button" disabled={saving} onClick={saveEdit}>
            {saving ? '수정 중...' : '수정 완료'}
          </button>
        </div>
      </div>
    </div>
  );

  const mainClassName =
    postType === 'place' ? 'subpage__main place-request' : postType === 'casting' ? 'subpage__main casting-request' : 'subpage__main';

  const retreatClassName = postType === 'casting' ? 'retreat casting' : 'retreat';

  return (
    <div className="mypage mypage--retreat-edit">
      <div className="inner">
        <MypageMenu />
        <div className="subpage__main">
          {loading ? (
            <div style={{ display: 'flex', justifyContent: 'center', padding: '50px' }}>
              <Loading />
            </div>
          ) : (
            <div className={retreatClassName}>
              <div className="inner">
                <main className={mainClassName}>
                  <div className="subpage__main__title">
                    <h3>{pageTitle}</h3>
                    <div className={postType === 'review' ? 'review-actions' : 'place__detail-actions'}>
                      <button className="btn btn--secondary" type="button" onClick={goBack}>
                        목록으로
                      </button>
                    </div>
                  </div>

                  {postType === 'place' && renderPlaceForm()}
                  {postType === 'casting' && renderCastingForm()}
                  {postType === 'review' && renderReviewForm()}
                </main>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
